// Sugalarm — voice bridge wrappers (cycle 16).
//
// NOTE (cycle 330): this build emits source maps, so comments in this file
// are readable by anyone inspecting the client. Nothing here — code OR
// comment — may name the upstream voice provider. The engine is a
// server-side detail behind `/api/tts/*`.
//
// `listVoices` returns the catalog the Voice section renders. Speech
// synthesis is POST-only (the request body carries the text + voice),
// so we can't point an `<audio>` element straight at the URL. Instead
// `fetchSpeechBlob` issues the POST through `authedFetch`, downloads
// the audio body as a Blob, returns an `objectURL` the caller assigns
// to the audio element, plus a `revoke()` helper the caller must
// invoke once playback ends — otherwise the blob URL leaks.

import { authedFetch, request } from "../lib/apiClient";
import type { ApiResult, VoiceListOut } from "../lib/types";

export function listVoices(
  localePrefix?: string,
): Promise<ApiResult<VoiceListOut>> {
  const qs = localePrefix
    ? `?locale_prefix=${encodeURIComponent(localePrefix)}`
    : "";
  return request<VoiceListOut>(`/api/tts/voices${qs}`);
}

export interface SpeechBlobHandle {
  url: string;
  revoke: () => void;
}

export async function fetchSpeechBlob(
  text: string,
  voice?: string | null,
): Promise<ApiResult<SpeechBlobHandle>> {
  const body: { text: string; voice?: string } = { text };
  if (voice && voice.trim()) body.voice = voice.trim();

  let res: Response;
  try {
    res = await authedFetch("/api/tts/speak", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch (e) {
    return {
      ok: false,
      error: {
        kind: "network",
        status: 0,
        message: e instanceof Error ? e.message : "network error",
      },
    };
  }
  if (!res.ok) {
    return {
      ok: false,
      error: {
        kind:
          res.status === 401
            ? "auth"
            : res.status === 422
              ? "validation"
              : res.status >= 500
                ? "server"
                : "unknown",
        status: res.status,
        message: res.statusText || `HTTP ${res.status}`,
      },
    };
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  return {
    ok: true,
    data: {
      url,
      revoke: () => URL.revokeObjectURL(url),
    },
  };
}

// ─── Cycle 309: TTS veracity-gate telemetry ─────────────────────────────
// Fire-and-forget report of a gate abort/rewrite/suppression so the fleet
// can be audited server-side (`sugalarm.tts.veracity` log tag). Bounded
// enum-ish strings only — never utterance text, never a member name.

export type VeracityEventKind =
  | "abort"
  | "rewrite_recovery"
  | "rewrite_stale"
  | "suppress_synthetic"
  | "suppress_resume"
  // Cycle 330: a scheduled announcement or a spoken reminder was skipped
  // because the member's voice quiet-hours window is open. Reuses this
  // existing seam rather than inventing a second reporting mechanism.
  // Mirrored in the server-side `VeracityEventIn` pattern.
  | "suppress_quiet_hours";

export function reportVeracityEvent(
  kind: VeracityEventKind,
  surface: string,
  templateClassification?: string | null,
  contextClassification?: string | null,
): void {
  void request<void>("/api/tts/veracity_event", {
    method: "POST",
    body: {
      kind,
      surface,
      template_classification: templateClassification ?? null,
      context_classification: contextClassification ?? null,
    },
  }).catch(() => {
    /* telemetry only — never disturb the speech path */
  });
}
